import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Link2, Loader2, Mail, ShieldCheck, Globe2, XCircle } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { api } from '@/lib/api'

interface AuthorizationRequest {
  request_id: string
  client_id: string
  client_name: string
  requested_scopes: string[]
  expires_at: string
}

interface DomainOption {
  id: string
  name: string
  status: string
}

interface MailboxOption {
  id: string
  email: string
  domain_id: string
  status: string
}

type ResourceMode = 'all' | 'domains' | 'mailboxes'

async function loadAll<T>(path: string, key: string): Promise<T[]> {
  const firstResponse = await api.get(`${path}?page=1&per_page=100`)
  const firstPage = firstResponse.data
  const totalPages = Number(firstPage.pagination?.total_pages || 1)
  if (totalPages <= 1) return firstPage[key] || []

  const remaining = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) => api.get(`${path}?page=${index + 2}&per_page=100`))
  )
  return [
    ...(firstPage[key] || []),
    ...remaining.flatMap((response) => response.data[key] || []),
  ]
}

/** CHECKPOINT: PRD-06 FR-6.5.3 — First-party consent with explicit domain/mailbox resource boundaries. */
export function ConnectedAppAuthorization() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const requestId = searchParams.get('request_id')
  const [authorizationRequest, setAuthorizationRequest] = useState<AuthorizationRequest | null>(null)
  const [domains, setDomains] = useState<DomainOption[]>([])
  const [mailboxes, setMailboxes] = useState<MailboxOption[]>([])
  const [resourceMode, setResourceMode] = useState<ResourceMode>('all')
  const [selectedDomains, setSelectedDomains] = useState<string[]>([])
  const [selectedMailboxes, setSelectedMailboxes] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!requestId) {
      setLoading(false)
      return
    }
    const load = async () => {
      try {
        const [requestResponse, domainsResponse, mailboxesResponse] = await Promise.all([
          api.get(`/oauth/authorize/${requestId}`),
          loadAll<DomainOption>('/domains', 'domains'),
          loadAll<MailboxOption>('/mailboxes', 'mailboxes'),
        ])
        setAuthorizationRequest(requestResponse.data)
        setDomains(domainsResponse)
        setMailboxes(mailboxesResponse)
      } catch {
        toast.error('This connection request is no longer available')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [requestId])

  const mailboxDomains = useMemo(() => new Map(domains.map((domain) => [domain.id, domain.name])), [domains])
  const requestsMailSend = authorizationRequest?.requested_scopes.includes('mail.send') ?? false

  const toggleSelection = (value: string, values: string[], update: (next: string[]) => void) => {
    update(values.includes(value) ? values.filter((id) => id !== value) : [...values, value])
  }

  const redirectClient = (redirectUrl: string) => {
    window.location.assign(redirectUrl)
  }

  const submitApproval = async () => {
    if (!requestId) return
    if (resourceMode === 'domains' && selectedDomains.length === 0) {
      toast.error('Select at least one domain, or choose all account resources')
      return
    }
    if (resourceMode === 'mailboxes' && selectedMailboxes.length === 0) {
      toast.error('Select at least one mailbox, or choose all account resources')
      return
    }
    setSubmitting(true)
    try {
      const resourceScope = resourceMode === 'domains'
        ? { allowed_domain_ids: selectedDomains }
        : resourceMode === 'mailboxes'
          ? { allowed_mailbox_ids: selectedMailboxes }
          : {}
      const response = await api.post(`/oauth/authorize/${requestId}/approve`, {
        approved: true,
        ...resourceScope,
      })
      redirectClient(response.data.redirect_url)
    } catch {
      toast.error('Could not approve this connection')
      setSubmitting(false)
    }
  }

  const deny = async () => {
    if (!requestId) return
    setSubmitting(true)
    try {
      const response = await api.post(`/oauth/authorize/${requestId}/deny`)
      redirectClient(response.data.redirect_url)
    } catch {
      toast.error('Could not deny this connection')
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="flex min-h-64 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading connection request</div>
  }

  if (!requestId || !authorizationRequest) {
    return (
      <div className="mx-auto max-w-lg py-8">
        <Card>
          <CardContent className="p-6 text-center">
            <XCircle className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">Connection request unavailable</p>
            <p className="mt-1 text-sm text-muted-foreground">It may have expired or already been completed.</p>
            <Button className="mt-4" variant="outline" onClick={() => navigate('/settings?tab=connected-apps')}>Back to settings</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-2">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Approve connected app</h1>
        <p className="mt-1 text-sm text-muted-foreground">Review what this app is requesting before you connect it to PurpleToad Mail.</p>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><Link2 className="h-4 w-4 text-muted-foreground" />{authorizationRequest.client_name}</CardTitle>
          <p className="font-mono text-xs text-muted-foreground">{authorizationRequest.client_id}</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <section>
            <p className="text-sm font-medium">Requested permissions</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {authorizationRequest.requested_scopes.map((scope) => <span key={scope} className="rounded-full border border-border bg-muted/40 px-2 py-1 font-mono text-xs">{scope}</span>)}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Permissions control what this app can do. Resource selection below limits where it can do it.</p>
          </section>

          {requestsMailSend && (
            <section className="flex gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium">This app is requesting permission to send email.</p>
                <p className="mt-1 text-xs text-muted-foreground">Approving this connection does not turn on automated sending. An account owner must separately enable it for this app in Connected apps after reviewing the risks.</p>
              </div>
            </section>
          )}

          <section className="border-t border-border pt-5">
            <p className="text-sm font-medium">Resource access</p>
            <p className="mt-1 text-xs text-muted-foreground">Choose the narrowest level that works. You can revoke this connection at any time in Settings.</p>
            <div className="mt-3 space-y-2">
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input className="mt-0.5 accent-primary" type="radio" name="resource-scope" checked={resourceMode === 'all'} onChange={() => setResourceMode('all')} />
                <span><span className="block text-sm font-medium">All account resources</span><span className="block text-xs text-muted-foreground">Allow the requested permissions across your account.</span></span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input className="mt-0.5 accent-primary" type="radio" name="resource-scope" checked={resourceMode === 'domains'} onChange={() => setResourceMode('domains')} />
                <span><span className="flex items-center gap-1.5 text-sm font-medium"><Globe2 className="h-3.5 w-3.5" />Selected domains</span><span className="block text-xs text-muted-foreground">Allow the app to reach only the selected domains and their mailboxes.</span></span>
              </label>
              {resourceMode === 'domains' && (
                <div className="ml-8 max-h-52 space-y-2 overflow-y-auto rounded-md border border-border p-3">
                  {domains.length === 0 ? <p className="text-xs text-muted-foreground">No domains are available for this account.</p> : domains.map((domain) => (
                    <label key={domain.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={selectedDomains.includes(domain.id)} onCheckedChange={() => toggleSelection(domain.id, selectedDomains, setSelectedDomains)} />
                      <span>{domain.name}</span><span className="text-xs text-muted-foreground">{domain.status}</span>
                    </label>
                  ))}
                </div>
              )}
              <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <input className="mt-0.5 accent-primary" type="radio" name="resource-scope" checked={resourceMode === 'mailboxes'} onChange={() => setResourceMode('mailboxes')} />
                <span><span className="flex items-center gap-1.5 text-sm font-medium"><Mail className="h-3.5 w-3.5" />Selected mailboxes</span><span className="block text-xs text-muted-foreground">Allow mailbox-specific tools only for the mailboxes you choose.</span></span>
              </label>
              {resourceMode === 'mailboxes' && (
                <div className="ml-8 max-h-52 space-y-2 overflow-y-auto rounded-md border border-border p-3">
                  {mailboxes.length === 0 ? <p className="text-xs text-muted-foreground">No mailboxes are available for this account.</p> : mailboxes.map((mailbox) => (
                    <label key={mailbox.id} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={selectedMailboxes.includes(mailbox.id)} onCheckedChange={() => toggleSelection(mailbox.id, selectedMailboxes, setSelectedMailboxes)} />
                      <span>{mailbox.email}</span><span className="text-xs text-muted-foreground">{mailboxDomains.get(mailbox.domain_id) || mailbox.status}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </section>

          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />PurpleToad Mail will not give this app your password. This request expires {new Date(authorizationRequest.expires_at).toLocaleTimeString()}.
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => void deny()} disabled={submitting}>Deny connection</Button>
            <Button onClick={() => void submitApproval()} disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Approve connection
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
